/**
 * OneVoiceTodayStrip - 3 curator-pinned editorial slots on Home.
 * The scarcity-drama of WSHH's front page, with the transparent-merit of
 * OneVoice (only top-rated reviewers + admins can pin).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Award, Star } from 'lucide-react';
import { AIBadge } from '../common/AIBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const OneVoiceTodayStrip = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/discovery/editorial/today`);
        if (!cancelled) setPins(res.data?.pins || []);
      } catch {
        /* fail-silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || pins.length === 0) return null;

  return (
    <section className="mb-10" data-testid="onevoice-today-strip">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="section-title-accent section-title-yellow font-['Chakra_Petch'] font-black uppercase text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
            <Award size={28} className="text-[#FFDA33]" /> OneVoice Today
          </h2>
          <p className="text-[#888] font-mono text-xs mt-1 uppercase tracking-[0.2em]">
            Picked today by top-rated curators · earned, not bought
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="onevoice-today-grid">
        {pins.map((p) => {
          const t = p.track || {};
          return (
            <Link
              key={p.pin_id}
              to={`/track/${t.id}`}
              className="relative bg-black/70 border border-[#FFDA33]/30 hover:border-[#FFDA33] transition-all overflow-hidden group"
              data-testid={`editorial-pin-${p.slot}`}
            >
              <div className="absolute top-0 left-0 px-2 py-1 bg-[#FFDA33] text-black font-['Chakra_Petch'] uppercase text-xs tracking-wider z-10">
                #{p.slot} Today
              </div>
              <div className="aspect-video md:aspect-[4/3] bg-[#111] relative overflow-hidden">
                {t.cover_art ? (
                  <img src={t.cover_art} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#333]">
                    <Award size={64} />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3">
                  <p className="text-white font-['Chakra_Petch'] text-lg leading-tight truncate">{t.title}</p>
                  <p className="text-[#888] font-mono text-xs truncate">{t.artist}</p>
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  {p.curator?.profile_picture ? (
                    <img src={p.curator.profile_picture} alt={p.curator.username} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[#FFDA33]/20 flex items-center justify-center">
                      <Star size={12} className="text-[#FFDA33]" />
                    </div>
                  )}
                  <span className="text-[#FFDA33] font-mono text-xs">Picked by @{p.curator?.username || 'admin'}</span>
                  {t.is_ai_content && <AIBadge size="xs" variant="subtle" />}
                </div>
                {p.note && (
                  <p className="text-[#aaa] font-mono text-xs leading-relaxed line-clamp-2">&ldquo;{p.note}&rdquo;</p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default OneVoiceTodayStrip;
